import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ShieldCheck, 
  AlertTriangle, 
  FileText, 
  MessageSquareWarning, 
  Lock, 
  Users, 
  ArrowRight, 
  CheckCircle2, 
  Bell, 
  Search, 
  Globe, 
  LifeBuoy, 
  Sparkles, 
  ExternalLink,
  ChevronRight,
  ShieldAlert
} from 'lucide-react';
import { ScoreGauge } from '../components/common/ScoreGauge';
import { StatusIndicator, SeverityBadge } from '../components/common/StatusIndicator';
import { useStore } from '../store/useStore';

export const Dashboard: React.FC = () => {
  const { user, securityScore, alerts, documents, familyMembers, recoveryCase, openAiModal } = useStore();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const activeAlerts = alerts.filter(a => !a.isResolved);
  const recentAlert = activeAlerts[0] || alerts[0];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      {/* Top Greeting & Main Status Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-xl">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-xs px-2.5 py-0.5 rounded-full bg-emerald-950 text-emerald-300 border border-emerald-800 font-semibold uppercase">
              Live Monitoring Active
            </span>
            <span className="text-xs text-slate-400 font-mono">ID: {user?.id || 'usr_au_98124'}</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white">
            {getGreeting()}, {user?.firstName || 'Alex'}
          </h1>
          <p className="text-xs text-slate-400">
            Continuous Australian telemetry active. Last synchronized {new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })} AEST.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={openAiModal}
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold flex items-center gap-1.5 transition-colors border border-slate-700"
          >
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span>AI Recovery Bot</span>
          </button>
          <Link
            to="/identity-theft"
            className="px-4 py-2 rounded-xl bg-rose-600/90 hover:bg-rose-500 text-white text-xs font-semibold flex items-center gap-1.5 transition-colors shadow-md"
          >
            <LifeBuoy className="w-4 h-4" />
            <span>Emergency Response</span>
          </Link>
        </div>
      </div>

      {/* Main Score & Core Status Banner */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Gauge Col */}
          <div className="lg:col-span-4 flex flex-col items-center justify-center text-center space-y-3 border-b lg:border-b-0 lg:border-r border-slate-800 pb-6 lg:pb-0 lg:pr-8">
            <ScoreGauge score={securityScore.overallScore} size="md" />
            <div>
              <h3 className="text-base font-bold text-white">Identity Security Score</h3>
              <p className="text-xs text-slate-400 mt-0.5">Calculated across 7 Australian security metrics</p>
            </div>
            <Link
              to="/security-score"
              className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-md transition-colors"
            >
              Improve My Score
            </Link>
          </div>

          {/* Quick Metrics Col */}
          <div className="lg:col-span-8 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Live Protection Pillars</span>
              <Link to="/security-score" className="text-xs text-emerald-400 hover:underline flex items-center gap-1 font-medium">
                <span>View Full Diagnostic Breakdown</span>
                <ChevronRight className="w-3 h-3" />
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">Email & Login</span>
                  <StatusIndicator status="good" />
                </div>
                <div className="text-lg font-bold text-white">90% Secure</div>
                <div className="text-[10px] text-slate-500">2FA Active</div>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">ID Documents</span>
                  <StatusIndicator status="good" />
                </div>
                <div className="text-lg font-bold text-white">{documents.length} Monitored</div>
                <div className="text-[10px] text-emerald-400">NSW Licence Linked</div>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">Credit Inquiries</span>
                  <StatusIndicator status="good" />
                </div>
                <div className="text-lg font-bold text-white">0 Inquiries</div>
                <div className="text-[10px] text-slate-500">Equifax / Experian</div>
              </div>
            </div>

            {/* Emergency Recovery in Progress Banner */}
            {recoveryCase && recoveryCase.completedStepsCount < recoveryCase.totalStepsCount && (
              <div className="p-4 rounded-xl bg-slate-950 border border-indigo-900/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse" />
                    <span className="font-bold text-indigo-200">Active Recovery Plan: {recoveryCase.incidentTitle}</span>
                  </div>
                  <div className="text-slate-400">
                    Progress: {recoveryCase.completedStepsCount} of {recoveryCase.totalStepsCount} recovery steps completed ({recoveryCase.progressPercentage}%).
                  </div>
                </div>
                <Link
                  to="/identity-theft"
                  className="px-3.5 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium text-xs whitespace-nowrap transition-colors"
                >
                  Resume Wizard
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CORE 5 DASHBOARD CARDS (Matching Requirement Section 8) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Card 1: Identity Monitoring */}
        <div className="p-6 rounded-2xl bg-slate-900 border border-slate-800 space-y-4 shadow-lg flex flex-col justify-between">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="p-2.5 rounded-xl bg-emerald-950 border border-emerald-800 text-emerald-400">
                <Search className="w-5 h-5" />
              </div>
              <StatusIndicator status="good" showLabel />
            </div>
            <h3 className="text-lg font-bold text-white">Identity Monitoring</h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              🟢 Continuous monitoring for email, Australian phone, and identity credentials across the dark web and breach databases.
            </p>
          </div>
          <Link
            to="/check-identity"
            className="pt-3 text-xs font-semibold text-emerald-400 hover:underline flex items-center gap-1 border-t border-slate-800"
          >
            <span>View Monitoring & Search</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Card 2: Recent Alerts */}
        <div className="p-6 rounded-2xl bg-slate-900 border border-slate-800 space-y-4 shadow-lg flex flex-col justify-between">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="p-2.5 rounded-xl bg-amber-950 border border-amber-800 text-amber-400">
                <Bell className="w-5 h-5" />
              </div>
              {activeAlerts.length > 0 ? (
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-amber-500/20 text-amber-300 font-bold border border-amber-800">
                  {activeAlerts.length} Active Alert
                </span>
              ) : (
                <StatusIndicator status="good" showLabel />
              )}
            </div>
            <h3 className="text-lg font-bold text-white">Recent Alerts</h3>
            {recentAlert ? (
              <div className="space-y-1">
                <p className="text-xs font-semibold text-amber-300">{recentAlert.title}</p>
                <p className="text-xs text-slate-400 line-clamp-2 leading-relaxed">
                  {recentAlert.whatHappened}
                </p>
              </div>
            ) : (
              <p className="text-xs text-slate-400">No active unaddressed alerts.</p>
            )}
          </div>
          <Link
            to="/alerts"
            className="pt-3 text-xs font-semibold text-amber-400 hover:underline flex items-center gap-1 border-t border-slate-800"
          >
            <span>View All Alerts ({alerts.length})</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Card 3: Account Security */}
        <div className="p-6 rounded-2xl bg-slate-900 border border-slate-800 space-y-4 shadow-lg flex flex-col justify-between">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="p-2.5 rounded-xl bg-cyan-950 border border-cyan-800 text-cyan-400">
                <Lock className="w-5 h-5" />
              </div>
              <StatusIndicator status="good" showLabel />
            </div>
            <h3 className="text-lg font-bold text-white">Account Security</h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              8 of 10 key accounts secured. Primary Gmail and NetBank have strong 2FA active. Superannuation account flagged for review.
            </p>
          </div>
          <Link
            to="/account-security"
            className="pt-3 text-xs font-semibold text-cyan-400 hover:underline flex items-center gap-1 border-t border-slate-800"
          >
            <span>Improve Account Security</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Card 4: ID Documents */}
        <div className="p-6 rounded-2xl bg-slate-900 border border-slate-800 space-y-4 shadow-lg flex flex-col justify-between">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="p-2.5 rounded-xl bg-blue-950 border border-blue-800 text-blue-400">
                <FileText className="w-5 h-5" />
              </div>
              <StatusIndicator status="good" showLabel />
            </div>
            <h3 className="text-lg font-bold text-white">ID Documents</h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              {documents.length} documents protected (NSW Driver Licence, Australian Passport). DVS card number verification safeguards active.
            </p>
          </div>
          <Link
            to="/identity-documents"
            className="pt-3 text-xs font-semibold text-blue-400 hover:underline flex items-center gap-1 border-t border-slate-800"
          >
            <span>Manage Australian Documents</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Card 5: Scam Protection */}
        <div className="p-6 rounded-2xl bg-slate-900 border border-slate-800 space-y-4 shadow-lg flex flex-col justify-between">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="p-2.5 rounded-xl bg-indigo-950 border border-indigo-800 text-indigo-400">
                <MessageSquareWarning className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-emerald-400 font-semibold bg-emerald-950 px-2 py-0.5 rounded border border-emerald-800">
                NO RECENT THREATS
              </span>
            </div>
            <h3 className="text-lg font-bold text-white">Scam Protection</h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              No suspicious activity detected. Analyze any unexpected SMS, email, toll reminder, or marketplace payment request.
            </p>
          </div>
          <Link
            to="/scam-checker"
            className="pt-3 text-xs font-semibold text-indigo-400 hover:underline flex items-center gap-1 border-t border-slate-800"
          >
            <span>Check a Suspicious Message</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Card 6: Family Protection */}
        <div className="p-6 rounded-2xl bg-slate-900 border border-slate-800 space-y-4 shadow-lg flex flex-col justify-between">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="p-2.5 rounded-xl bg-purple-950 border border-purple-800 text-purple-400">
                <Users className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-purple-300 font-semibold bg-purple-950 px-2 py-0.5 rounded border border-purple-800">
                {familyMembers.length} MEMBERS PROTECTED
              </span>
            </div>
            <h3 className="text-lg font-bold text-white">Family Protection</h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              Family security shield active for partner, child, and elderly parents with privacy-respecting breach alerts.
            </p>
          </div>
          <Link
            to="/family"
            className="pt-3 text-xs font-semibold text-purple-400 hover:underline flex items-center gap-1 border-t border-slate-800"
          >
            <span>Open Family Dashboard</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {/* QUICK LAUNCHPAD / TOOLS BAR */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
        <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider">Quick Protection Tools</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Link
            to="/id-watermark"
            className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 hover:border-emerald-700/60 transition-colors flex items-center gap-3 text-xs text-slate-200"
          >
            <FileText className="w-4 h-4 text-emerald-400 flex-shrink-0" />
            <div className="font-semibold">ID Watermark Tool</div>
          </Link>

          <Link
            to="/website-checker"
            className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 hover:border-blue-700/60 transition-colors flex items-center gap-3 text-xs text-slate-200"
          >
            <Globe className="w-4 h-4 text-blue-400 flex-shrink-0" />
            <div className="font-semibold">Website Checker</div>
          </Link>

          <Link
            to="/identity-theft"
            className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 hover:border-rose-700/60 transition-colors flex items-center gap-3 text-xs text-slate-200"
          >
            <LifeBuoy className="w-4 h-4 text-rose-400 flex-shrink-0" />
            <div className="font-semibold">8-Step Recovery</div>
          </Link>

          <Link
            to="/resources"
            className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 hover:border-purple-700/60 transition-colors flex items-center gap-3 text-xs text-slate-200"
          >
            <ShieldCheck className="w-4 h-4 text-purple-400 flex-shrink-0" />
            <div className="font-semibold">Australian Guides</div>
          </Link>
        </div>
      </div>
    </div>
  );
};
